import {
  all,
  put,
  putResolve,
  select,
  takeLatest,
} from 'redux-saga/effects';
import some from 'lodash/some';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import has from 'lodash/has';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import { updateChoices as updateConfiguratorChoices } from '@gcc/configurator/dist/actions/choiceActions';
import {
  CHOICE_SELECTED,
  setSelections,
  setAvailableChoices,
  updateSelectedChoice,
  clearPageError
} from '../actions';
import { updatePath } from '../actions/pathActions';
import safeSaga from './safeSaga';
import {
  findConfiguratorChoiceBySkuSelector,
  skuDataSelector,
  selectOptionsAndChoicesByOmsId
} from '../selectors/skuDataSelectors';
import { choiceSelectionsSelector } from '../selectors/selections';
import { orderedDependentOptionsSelector, configuratorChoices } from '../selectors/configurator';

const isNewSelection = (selections, choice) => {
  return some(selections, (selection) => {
    return selection.optionId !== choice.optionId
      && selection.choiceId !== choice.choiceId
      && selection.choiceValue !== choice.value;
  });
};

const findMatchedOptionChoicesInSku = (dependentOptionsArray, skuOptionChoices, selections) => {
  return filter(skuOptionChoices, (optionChoice) => {
    const { optionId, choiceId } = optionChoice;
    const selectionsHasMatchingOption = has(selections, optionId);
    const isDepdendentOption = dependentOptionsArray.includes(optionId);
    return selectionsHasMatchingOption && isDepdendentOption
      && optionId === selections[optionId].optionId
      && choiceId === selections[optionId].choiceId;
  });
};
const validateSelection = (dependentOptions, skuData, newSelections) => {
  const validSku = filter(skuData, (sku) => {
    const { optionChoices } = sku;
    const choices = findMatchedOptionChoicesInSku(dependentOptions, optionChoices, newSelections);
    return choices.length === dependentOptions.length;
  });
  return validSku;
};

const findFallBackOption = (dependentOptions, skuData, newSelections, optionId, allChoices) => {
  let fallbackOptionId;
  const validSku = filter(skuData, (sku) => {
    const { optionChoices } = sku;
    const choices = findMatchedOptionChoicesInSku(dependentOptions, optionChoices, newSelections);

    if (some(choices, (choice) => choice.optionId === optionId)) {
      const options = choices.map((ch) => ch.optionId);
      fallbackOptionId = find(dependentOptions, (optId) => !options.includes(optId));
      return sku;
    }
    return null;
  });
  const allChoicesByOptionId =
    sortBy(filter(allChoices, (allChoice) => allChoice.optionId === fallbackOptionId), 'value');
  const final = allChoicesByOptionId.map((item) => find(validSku, (sku) => {
    return find(validSku,
      ({ optionChoices }) => optionChoices.optionId === sku.optionId && optionChoices.choiceId === sku.id);
  }));
  return final[0];
};

function* setChoiceSelections({ payload: choice }) {
  const previousSelections = yield select(choiceSelectionsSelector);
  const { optionId, id, value } = choice;

  if (isNewSelection(previousSelections, choice)) {
    yield put(clearPageError());
    let newSelections = { ...previousSelections, [optionId]: { optionId, choiceId: id, value } };
    const orderedOptionIds = yield select(orderedDependentOptionsSelector('SuperSku'));
    const skuData = yield select(skuDataSelector);
    const isValidSku = validateSelection(orderedOptionIds, skuData, newSelections);
    let newSku = isValidSku[0]?.sku;
    if (isEmpty(isValidSku)) {
      const configChoices = yield select(configuratorChoices);
      const newValidSkuData = findFallBackOption(orderedOptionIds, skuData, newSelections, optionId, configChoices);
      newSku = newValidSkuData.sku;
    }
    const newSkuSelections = yield select(selectOptionsAndChoicesByOmsId(newSku));

    const selectionDifferences = filter(newSkuSelections, (skuSelection) => {
      return some(previousSelections, (prevSelection) => {
        return skuSelection.optionId === prevSelection.optionId && skuSelection.choiceId !== prevSelection.choiceId;
      });
    });

    const { choiceId: colorChoiceId } = yield select(findConfiguratorChoiceBySkuSelector(newSku));
    yield all([
      yield put(setSelections(newSkuSelections)),
      yield put(updateSelectedChoice({ id: colorChoiceId, sku: newSku })),
      yield put(setAvailableChoices()),
      yield putResolve(updateConfiguratorChoices(selectionDifferences)), // required to update configurator choices
      yield put(updatePath())
    ]);
  }
}

export function* selectedChoiceSaga() {
  yield takeLatest(CHOICE_SELECTED, safeSaga(setChoiceSelections));
}
